import { Loader2, Volume2 } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { IStep } from '@chainlit/react-client';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface FeedbackButtonsProps {
  message: IStep;
}

interface Progress {
  done: number;
  total: number;
}

export function FeedbackButtons({ message }: FeedbackButtonsProps) {
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<string | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const prevBlobRef = useRef<string | null>(null);

  if (!message.output || message.streaming) return null;

  const handleSpeak = useCallback(async () => {
    if (busy) return;

    if (prevBlobRef.current) {
      URL.revokeObjectURL(prevBlobRef.current);
      prevBlobRef.current = null;
    }
    setAudioSrc(null);
    setError(null);
    setProgress(null);
    setStage(null);
    setBusy(true);

    try {
      const response = await fetch('/api/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({ text: message.output })
      });

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      outer: while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data: ')) continue;
          const data = JSON.parse(line.slice(6));

          if (data.type === 'stage') {
            setStage(data.label);
          } else if (data.type === 'start') {
            setStage(null);
            setProgress({ done: 0, total: data.total });
          } else if (data.type === 'progress') {
            setProgress({ done: data.done, total: Math.max(data.total, data.done) });
          } else if (data.type === 'done') {
            const bytes = Uint8Array.from(atob(data.audio), (c) => c.charCodeAt(0));
            const blob = new Blob([bytes], { type: 'audio/wav' });
            const url = URL.createObjectURL(blob);
            prevBlobRef.current = url;
            setAudioSrc(url);
            break outer;
          } else if (data.type === 'error') {
            throw new Error(data.message);
          }
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      setProgress(null);
      setStage(null);
    }
  }, [busy, message.output]);

  const pct =
    progress && progress.total > 0
      ? Math.round((progress.done / progress.total) * 100)
      : 0;

  const tooltipText = busy
    ? (stage ?? (progress ? `Synthesizing… (${progress.done}/${progress.total})` : 'Starting…'))
    : 'Read aloud';

  return (
    <>
      {/* Speaker button — stays inline in the buttons row */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSpeak}
              disabled={busy}
              className="text-muted-foreground"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{tooltipText}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {error && (
        <span className="text-xs text-red-500 max-w-[200px] truncate" title={error}>
          {error}
        </span>
      )}

      {/* Progress bar + audio player — w-full forces a new row in the flex-wrap parent */}
      {(busy && progress !== null) || audioSrc ? (
        <div className="w-full flex flex-col gap-2 mt-2.5">
          {busy && progress !== null && (
            <div className="w-full bg-muted rounded-full h-1 overflow-hidden">
              <div
                className="h-1 bg-primary rounded-full transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
          )}
          {audioSrc && (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio key={audioSrc} controls autoPlay src={audioSrc} className="w-full" />
          )}
        </div>
      ) : null}
    </>
  );
}
